/**
 * Single source of truth for "does this href leave the app?" and the attributes an
 * outbound link needs. Used by `components/anchor` and by non-React code that emits
 * anchors (e.g. CMS HTML post-processing).
 */

import { WORKER_BASE_URL } from '../constants'

export interface ExternalLinkAttributes {
  target?: string
  rel?: string
}

const EXTERNAL_TARGET = '_blank'

// Base for resolving relative hrefs when the app origin is unknown (SSR without
// GATSBY_SITE_URL). Anything resolving to it is, by definition, relative.
const RELATIVE_BASE = 'http://relative.invalid'

const stripWww = (host: string): string => host.replace(/^www\./i, '')

/** The browser's origin, or the configured site URL during SSR. */
const getCurrentOrigin = (): string | undefined =>
  typeof window !== 'undefined'
    ? window.location.origin
    : process.env.GATSBY_SITE_URL

/**
 * True when `href` is an http(s) URL on a different host than the app. Relative paths,
 * hash links, `mailto:`/`tel:` and the app's own host (with or without `www.`) are internal.
 */
export const isExternalUrl = (
  href?: string | null,
  origin: string | undefined = getCurrentOrigin()
): boolean => {
  const value = href?.trim()
  if (!value || value.startsWith('#')) return false

  try {
    const base = new URL(origin || RELATIVE_BASE)
    const url = new URL(value, base)
    const isHttp = url.protocol === 'http:' || url.protocol === 'https:'
    return isHttp && stripWww(url.host) !== stripWww(base.host)
  } catch (e) {
    return false
  }
}

// Our redirect worker (go.chirpyest.com) attributes clicks using the Referer header, so links
// to it must never carry `noreferrer`.
const getWorkerHost = (): string | undefined => {
  try {
    return new URL(WORKER_BASE_URL).host
  } catch (e) {
    return undefined
  }
}

export const isWorkerUrl = (href?: string | null): boolean => {
  try {
    return !!href && new URL(href.trim()).host === getWorkerHost()
  } catch (e) {
    return false
  }
}

const relTokens = (...rels: Array<string | undefined>): string[] =>
  Array.from(
    new Set(
      rels
        .join(' ')
        .split(/\s+/)
        .filter(Boolean)
    )
  )

/**
 * Attributes for an anchor pointing at `href`. External links always get
 * `target="_blank"` and `rel="noopener noreferrer"` (merged with any caller `rel`), except
 * links to our redirect worker, which keep the Referer (no `noreferrer`) and are marked
 * `sponsored`: every worker link is an affiliate link, which Google asks to be labelled.
 * Internal links get back exactly what the caller passed.
 */
export const getExternalLinkProps = (
  href: string | null | undefined,
  { target, rel }: ExternalLinkAttributes = {}
): ExternalLinkAttributes => {
  if (!isExternalUrl(href)) return { target, rel }

  const tokens = relTokens('noopener noreferrer', rel)
  return {
    target: EXTERNAL_TARGET,
    rel: (isWorkerUrl(href)
      ? relTokens(tokens.filter(t => t !== 'noreferrer').join(' '), 'sponsored')
      : tokens
    ).join(' '),
  }
}
